import api from "./api-utils";
import { Temporal } from "@js-temporal/polyfill";
import {
  DAYS_7_IN_HOURS,
  DAYS_30_IN_HOURS,
  DAYS_60_IN_HOURS,
  HOURS_24,
  utcIsoToLocalIso,
} from "@/lib/datetime-utils.js";

/**
 * TIMEZONE NOTE: All date parameters (StartDate, EndDate) must be in UTC format (ISO 8601 strings).
 * Use .toISOString() when creating Date objects to ensure UTC format.
 * The API expects and returns all timestamps in UTC.
 */

export const getTagMeasurements = async (requestObject) => {
  const response = await api.post("/api/measurement/find", requestObject);
  return response.data;
};

/**
 * Get tag measurements for the last 24 hours
 * Dates are converted to UTC ISO strings for API compatibility
 */
export const getTagMeasurementsLast24Hours = (assetTagId, assetId) => {
  const requestObject = {
    AssetTagId: assetTagId,
    AssetIdentifier: assetId,
    // Convert to UTC ISO string for API
    StartDate: Temporal.Now.instant()
      .subtract(HOURS_24)
      .toString({ smallestUnit: "millisecond" }),
    EndDate: Temporal.Now.instant().toString({ smallestUnit: "millisecond" }),
  };
  return getTagMeasurements(requestObject);
};

/**
 * Get tag measurements for the last 7 days
 * Dates are converted to UTC ISO strings for API compatibility
 */
export const getTagMeasurementsLast7Days = (assetTagId, assetId) => {
  const requestObject = {
    AssetTagId: assetTagId,
    AssetIdentifier: assetId,
    // Convert to UTC ISO string for API
    StartDate: Temporal.Now.instant()
      .subtract(DAYS_7_IN_HOURS)
      .toString({ smallestUnit: "millisecond" }),
    EndDate: Temporal.Now.instant().toString({ smallestUnit: "millisecond" }),
  };
  return getTagMeasurements(requestObject);
};

/**
 * Get tag measurements for the last 30 days
 * Dates are converted to UTC ISO strings for API compatibility
 */
export const getTagMeasurementsLast30Days = (assetTagId, assetId) => {
  const requestObject = {
    AssetTagId: assetTagId,
    AssetIdentifier: assetId,
    // Convert to UTC ISO string for API
    StartDate: Temporal.Now.instant()
      .subtract(DAYS_30_IN_HOURS)
      .toString({ smallestUnit: "millisecond" }),
    EndDate: Temporal.Now.instant().toString({ smallestUnit: "millisecond" }),
  };
  return getTagMeasurements(requestObject);
};

/**
 * Get tag measurements for the last 60 days
 * Dates are converted to UTC ISO strings for API compatibility
 */
export const getTagMeasurementsLast60Days = (assetTagId, assetId) => {
  const requestObject = {
    AssetTagId: assetTagId,
    AssetIdentifier: assetId,
    // Convert to UTC ISO string for API
    StartDate: Temporal.Now.instant()
      .subtract(DAYS_60_IN_HOURS)
      .toString({ smallestUnit: "millisecond" }),
    EndDate: Temporal.Now.instant().toString({ smallestUnit: "millisecond" }),
  };
  return getTagMeasurements(requestObject);
};

/**
 * One timestamped measurement sample for a tag (maps to `MeasurementPointValueResponseDTO` on the API).
 *
 * @typedef {Object} MeasurementPointValueResponseDTO
 * @property {string} DateTime - Sample time in UTC (ISO 8601), e.g. `"2026-05-12T10:15:00Z"`.
 * @property {number} Value - Numeric value at this time (API uses `decimal`; JSON is a number).
 * @property {string} [TextValue] - Present when the underlying value is textual or formatted as text (C# `string?`).
 */

/**
 * Per-tag rollup for the requested window: min/max/average, anomaly count, z-score of the latest point,
 * and the time series (maps to `MeasurementWithStatsResponseDTO` on the API).
 *
 * @typedef {Object} MeasurementWithStatsResponseDTO
 * @property {number} AssetTagId - Tag identifier this row summarizes.
 * @property {string} TagDescription - Tag description for displaying the tag and its trends.
 * @property {string} [UoM] - Unit of measure for the tag (short label, e.g. from API).
 * @property {number} LoLo - The LoLo Alarm limit.
 * @property {number} Lo - The Lo Alarm limit.
 * @property {number} Hi - The Hi Alarm limit.
 * @property {number} HiHi - The HiHi alarm limit.
 * @property {number} Min - Minimum observed value in the window.
 * @property {number} Max - Maximum observed value in the window.
 * @property {number} Avg - Mean of values in the window.
 * @property {number} ZScore - Z-score of the most recent measurement vs the series in the window.
 * @property {number} StdDev - Standard deviation of values over the selected timespan.
 * @property {number} Anomalies - Count of anomalies detected in the window (non-negative integer).
 * @property {MeasurementPointValueResponseDTO[]} Measurements - Chronological points for this tag.
 */

/**
 * Get tag measurements and statistical summary values for every tag on an asset over a time range.
 * Use UTC ISO strings for `startDate` and `endDate` (see module note above).
 *
 * @param {string} assetId - Asset identifier sent as `AssetIdentifier` in the request body.
 * @param {string} startDate - Range start in UTC ISO 8601 (e.g. from `Temporal` or `Date.toISOString()`).
 * @param {string} endDate - Range end in UTC ISO 8601.
 * @returns {Promise<MeasurementWithStatsResponseDTO[]>} One object per tag on the asset; property names match the API (PascalCase).
 *
 * @example
 * // Example response shape:
 * // [
 * //   {
 * //     "AssetTagId": 2525064,
 * //     "Min": 1126.3,
 * //     "Max": 1145,
 * //     "Avg": 1134.7266666666667,
 * //     "ZScoreValue": -0.733824897927974,
 * //     "UoM": "psi",
 * //     "LoLo": 1100,
 * //     "Lo": 1120,
 * //     "Hi": 1180,
 * //     "HiHi": 1200,
 * //     "StdDev": 4.2,
 * //     "Anomalies": 19,
 * //     "Measurements": [
 * //       { "DateTime": "2026-05-12T10:15:00Z", "Value": 1132.9 },
 * //       { "DateTime": "2026-05-12T10:26:00Z", "Value": 1128.6 }
 * //     ]
 * //   }
 * // ]
 */

/**
 * @param {MeasurementWithStatsResponseDTO[]} rows
 * @returns {MeasurementWithStatsResponseDTO[]}
 */
function localizeRcaMeasurementRowsDateTimesFromUtc(rows) {
  return rows.map((row) => ({
    ...row,
    Measurements: row.Measurements.map((point) => ({
      ...point,
      DateTime: utcIsoToLocalIso(point.DateTime),
    })),
  }));
}

/**
 * Get tag measurements for RCA. The HTTP payload is mapped before return (same idea as
 * `get30DayRunstatusAlarmHistoryForAsset` in `alarm-api.js`): each `Measurements[].DateTime` is converted
 * from UTC to the runtime local time zone (same instant, ISO with offset).
 *
 * @param {string} assetId
 * @param {string} startDate
 * @param {string} endDate
 * @returns {Promise<MeasurementWithStatsResponseDTO[]>}
 */
export const getTagMeasurementsForRCA = async (assetId, startDate, endDate) => {
  const requestObject = {
    AssetIdentifier: assetId,
    StartDate: startDate,
    EndDate: endDate,
  };
  const response = await api.post(
    "/api/measurement/find-per-asset",
    requestObject
  );

  const rows = Array.isArray(response.data) ? response.data : [];
  return localizeRcaMeasurementRowsDateTimesFromUtc(rows);
};
